import { useState, useEffect } from 'react';
import axios from 'axios';
import { Sidebar } from '../components/Sidebar';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Loader2, Settings, Save, Sliders } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SettingsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [targets, setTargets] = useState(null);

  useEffect(() => {
    fetchLocations();
  }, []);

  useEffect(() => {
    if (selectedLocation) {
      fetchTargets(selectedLocation);
    }
  }, [selectedLocation]);

  const fetchLocations = async () => {
    try {
      const res = await axios.get(`${API}/locations`);
      setLocations(res.data);
      if (res.data.length > 0) {
        setSelectedLocation(res.data[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch locations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTargets = async (locationId) => {
    try {
      const res = await axios.get(`${API}/targets/${locationId}`);
      setTargets(res.data);
    } catch (error) {
      console.error('Failed to fetch targets:', error);
      setTargets(null);
    }
  };

  const handleTargetChange = (field, value) => {
    setTargets(prev => ({
      ...prev,
      [field]: parseFloat(value) || 0
    }));
  };

  const handleSave = async () => {
    if (!targets || !selectedLocation) return;

    setSaving(true);
    try {
      await axios.put(`${API}/targets/${selectedLocation}`, {
        budget_attainment_target: targets.budget_attainment_target,
        revenue_per_labor_hour_target: targets.revenue_per_labor_hour_target,
        transactions_per_labor_hour_target: targets.transactions_per_labor_hour_target,
        atv_target: targets.atv_target,
        capture_rate_target: targets.capture_rate_target,
        seen_rate_target: targets.seen_rate_target,
        overall_sales_target: targets.overall_sales_target,
        per_cap_sales_target: targets.per_cap_sales_target,
        supplemental_sales_pct_target: targets.supplemental_sales_pct_target,
        digital_mix_target: targets.digital_mix_target,
        budget_labor_weight: targets.budget_labor_weight,
        conversion_weight: targets.conversion_weight,
        revenue_quality_weight: targets.revenue_quality_weight,
        capture_weight: targets.capture_weight,
        product_mix_weight: targets.product_mix_weight
      });
      toast.success('Targets saved successfully');
    } catch (error) {
      toast.error('Failed to save targets');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-violet-50 via-white to-fuchsia-50">
      <Sidebar />
      
      <main className="flex-1 p-4 lg:p-8" data-testid="settings-page">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 font-['Outfit']">
            Settings
          </h1>
          <p className="text-slate-600 mt-1">Configure targets and scoring weights</p>
        </div>

        {/* Location Selector */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="space-y-2">
              <Label>Select Location</Label>
              <Select value={selectedLocation} onValueChange={setSelectedLocation}>
                <SelectTrigger className="w-full max-w-md" data-testid="settings-location-select">
                  <SelectValue placeholder="Select a location" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map(loc => (
                    <SelectItem key={loc.id} value={loc.id}>
                      {loc.name} ({loc.code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {targets && (
          <>
            {/* Metric Targets */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Settings className="w-5 h-5 text-violet-600" />
                  Metric Targets
                </CardTitle>
                <CardDescription>Set performance targets for this location</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="space-y-2">
                    <Label>Budget Attainment (%)</Label>
                    <Input
                      type="number"
                      value={targets.budget_attainment_target}
                      onChange={(e) => handleTargetChange('budget_attainment_target', e.target.value)}
                      className="font-mono"
                      data-testid="input-budget-target"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Revenue per Labor Hour ($)</Label>
                    <Input
                      type="number"
                      value={targets.revenue_per_labor_hour_target}
                      onChange={(e) => handleTargetChange('revenue_per_labor_hour_target', e.target.value)}
                      className="font-mono"
                      data-testid="input-rplh-target"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Transactions per Labor Hour</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={targets.transactions_per_labor_hour_target}
                      onChange={(e) => handleTargetChange('transactions_per_labor_hour_target', e.target.value)}
                      className="font-mono"
                      data-testid="input-tplh-target"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Average Transaction Value ($)</Label>
                    <Input
                      type="number"
                      value={targets.atv_target}
                      onChange={(e) => handleTargetChange('atv_target', e.target.value)}
                      className="font-mono"
                      data-testid="input-atv-target"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Capture Rate (%)</Label>
                    <Input
                      type="number"
                      value={targets.capture_rate_target}
                      onChange={(e) => handleTargetChange('capture_rate_target', e.target.value)}
                      className="font-mono"
                      data-testid="input-capture-target"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Seen Rate (%)</Label>
                    <Input
                      type="number"
                      value={targets.seen_rate_target}
                      onChange={(e) => handleTargetChange('seen_rate_target', e.target.value)}
                      className="font-mono"
                      data-testid="input-seen-target"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Overall Sales % (PAX)</Label>
                    <Input
                      type="number"
                      value={targets.overall_sales_target}
                      onChange={(e) => handleTargetChange('overall_sales_target', e.target.value)}
                      className="font-mono"
                      data-testid="input-overall-sales-target"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Per Cap Sales ($)</Label>
                    <Input
                      type="number"
                      value={targets.per_cap_sales_target}
                      onChange={(e) => handleTargetChange('per_cap_sales_target', e.target.value)}
                      className="font-mono"
                      data-testid="input-percap-target"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Product Mix Targets */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Settings className="w-5 h-5 text-blue-600" />
                  Product Mix Targets
                </CardTitle>
                <CardDescription>Set targets for product and package mix analysis</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>Supplemental Sales % Target</Label>
                    <Input
                      type="number"
                      value={targets.supplemental_sales_pct_target}
                      onChange={(e) => handleTargetChange('supplemental_sales_pct_target', e.target.value)}
                      className="font-mono"
                      data-testid="input-supp-pct-target"
                    />
                    <p className="text-xs text-slate-500">Target percentage of total sales from supplemental products</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Digital Mix % Target</Label>
                    <Input
                      type="number"
                      value={targets.digital_mix_target}
                      onChange={(e) => handleTargetChange('digital_mix_target', e.target.value)}
                      className="font-mono"
                      data-testid="input-digital-mix-target"
                    />
                    <p className="text-xs text-slate-500">Target percentage of supplemental sales that are digital</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Scoring Weights */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Sliders className="w-5 h-5 text-violet-600" />
                  Scoring Weights
                </CardTitle>
                <CardDescription>Adjust how each category impacts the overall score (must total 100%)</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
                  <div className="space-y-2">
                    <Label>Budget & Labor (%)</Label>
                    <Input
                      type="number"
                      value={targets.budget_labor_weight}
                      onChange={(e) => handleTargetChange('budget_labor_weight', e.target.value)}
                      className="font-mono"
                      data-testid="input-budget-weight"
                    />
                    <p className="text-xs text-slate-400">Default: 30%</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Conversion (%)</Label>
                    <Input
                      type="number"
                      value={targets.conversion_weight}
                      onChange={(e) => handleTargetChange('conversion_weight', e.target.value)}
                      className="font-mono"
                      data-testid="input-conversion-weight"
                    />
                    <p className="text-xs text-slate-400">Default: 25%</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Revenue Quality (%)</Label>
                    <Input
                      type="number"
                      value={targets.revenue_quality_weight}
                      onChange={(e) => handleTargetChange('revenue_quality_weight', e.target.value)}
                      className="font-mono"
                      data-testid="input-revenue-weight"
                    />
                    <p className="text-xs text-slate-400">Default: 20%</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Product Mix (%)</Label>
                    <Input
                      type="number"
                      value={targets.product_mix_weight}
                      onChange={(e) => handleTargetChange('product_mix_weight', e.target.value)}
                      className="font-mono"
                      data-testid="input-product-mix-weight"
                    />
                    <p className="text-xs text-slate-400">Default: 15%</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Capture (%)</Label>
                    <Input
                      type="number"
                      value={targets.capture_weight}
                      onChange={(e) => handleTargetChange('capture_weight', e.target.value)}
                      className="font-mono"
                      data-testid="input-capture-weight"
                    />
                    <p className="text-xs text-slate-400">Default: 10%</p>
                  </div>
                </div>
                
                <div className="mt-4 p-3 rounded-lg bg-slate-50 text-sm">
                  <span className="font-medium text-slate-700">Current Total: </span>
                  <span className={`font-mono ${
                    ((targets.budget_labor_weight || 0) + (targets.conversion_weight || 0) + (targets.revenue_quality_weight || 0) + (targets.capture_weight || 0) + (targets.product_mix_weight || 0)) === 100
                      ? 'text-emerald-600'
                      : 'text-red-600'
                  }`}>
                    {(targets.budget_labor_weight || 0) + (targets.conversion_weight || 0) + (targets.revenue_quality_weight || 0) + (targets.capture_weight || 0) + (targets.product_mix_weight || 0)}%
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* Save Button */}
            <Button
              onClick={handleSave}
              className="w-full md:w-auto bg-violet-600 hover:bg-violet-700"
              disabled={saving}
              data-testid="save-settings-btn"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save Settings
            </Button>
          </>
        )}
      </main>
    </div>
  );
}
